/**
 * Printer Setup Tab Component
 * Manages receipt printer configuration and setup
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Printer, 
  Monitor, 
  CheckCircle, 
  AlertCircle,
  Save,
  Eye,
  EyeOff,
  Copy
} from 'lucide-react';
import { SuperAdminOnboardingData, getStatusBadgeVariant, getStatusDisplayName } from '@/types/super-admin-onboarding';
import { OnboardingStatus, SetupMethod } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

interface PrinterSetupTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const PrinterSetupTab: React.FC<PrinterSetupTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  const [formData, setFormData] = useState({
    printer_setup_method: data.printer_setup_method || SetupMethod.REMOTE_ACCESS,
    teamviewer_id: data.teamviewer_id || '',
    teamviewer_password: data.teamviewer_password || ''
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPassword, setShowPassword] = useState(true); // Default to showing password

  // Handle form field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Save all changes
  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate(formData);
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: `Updated ${result.changedCount} field(s) successfully`
          });
        }
      } else {
        // Fallback to individual updates if bulk update not available
        const changedFields = Object.entries(formData).filter(([field, value]) => {
          const originalValue = data[field as keyof SuperAdminOnboardingData];
          return originalValue !== value;
        });
        
        if (changedFields.length === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
          return;
        }
        
        const updates = changedFields.map(([field, value]) => 
          onFieldUpdate(field as keyof SuperAdminOnboardingData, value)
        );
        
        await Promise.all(updates);
        
        toast({
          title: "Success",
          description: `Updated ${changedFields.length} field(s) successfully`
        });
      }
      
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save printer setup information",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Update status
  const handleStatusUpdate = async (status: OnboardingStatus) => {
    try {
      // Save changes first if any
      if (hasChanges) {
        await handleSave();
      }
      
      await onFieldUpdate('printer_setup_status', status);
      
      toast({
        title: "Success",
        description: `Printer setup status updated to ${getStatusDisplayName(status)}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  // Copy TeamViewer details
  const handleCopyTeamViewerDetails = async () => {
    const details = `TeamViewer Access Details:
ID: ${formData.teamviewer_id}
Password: ${formData.teamviewer_password}`;

    try {
      await navigator.clipboard.writeText(details);
      toast({
        title: "Copied",
        description: "TeamViewer details copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy TeamViewer details",
        variant: "destructive"
      });
    }
  };

  const isComplete = data.printer_setup_status === OnboardingStatus.COMPLETE;
  const hasRequiredInfo = formData.printer_setup_method === SetupMethod.DIY || 
                         (formData.teamviewer_id && formData.teamviewer_password);

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Printer className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Printer Setup</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Configure receipt printer for order processing
                </p>
              </div>
            </div>
            <Badge variant={getStatusBadgeVariant(data.printer_setup_status || OnboardingStatus.NOT_STARTED)}>
              {getStatusDisplayName(data.printer_setup_status || OnboardingStatus.NOT_STARTED)}
            </Badge>
          </div>
        </CardHeader>
      </Card>


      {/* Setup Method */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Setup Method
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Printer Setup Method</Label>
            <Select
              value={formData.printer_setup_method}
              onValueChange={(value) => handleFieldChange('printer_setup_method', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select setup method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SetupMethod.REMOTE_ACCESS}>Remote Access Setup</SelectItem>
                <SelectItem value={SetupMethod.DIY}>Customer Self-Setup</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.printer_setup_method === SetupMethod.REMOTE_ACCESS ? (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Remote Access Setup:</strong> Our technical team will connect remotely to configure the printer and ensure everything works correctly.
              </p>
            </div>
          ) : (
            <div className="p-4 bg-green-50 dark:bg-green-950/50 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-200">
                <strong>Customer Self-Setup:</strong> The customer will set up the printer themselves using our provided documentation and support.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* TeamViewer Access (for Remote Setup) */}
      {formData.printer_setup_method === SetupMethod.REMOTE_ACCESS && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              Remote Access Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-950/50 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Note:</strong> The customer will need to install TeamViewer and provide these access details for remote printer setup.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="teamviewer_id">TeamViewer ID</Label>
                <Input
                  id="teamviewer_id"
                  value={formData.teamviewer_id}
                  onChange={(e) => handleFieldChange('teamviewer_id', e.target.value)}
                  placeholder="123 456 789"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="teamviewer_password">TeamViewer Password</Label>
                <div className="relative">
                  <Input
                    id="teamviewer_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.teamviewer_password}
                    onChange={(e) => handleFieldChange('teamviewer_password', e.target.value)}
                    placeholder="••••••••"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {formData.teamviewer_id && formData.teamviewer_password && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">TeamViewer Access Summary</h4>
                  <Button variant="outline" size="sm" onClick={handleCopyTeamViewerDetails}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Details
                  </Button>
                </div>
                <div className="text-sm space-y-1 font-mono">
                  <div>ID: {formData.teamviewer_id}</div>
                  <div>Password: {showPassword ? formData.teamviewer_password : '••••••••'}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.printer_setup_method === SetupMethod.REMOTE_ACCESS ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <div>
                  <h4 className="font-medium">Install TeamViewer</h4>
                  <p className="text-sm text-muted-foreground">
                    Customer downloads and installs TeamViewer on their POS system.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div>
                  <h4 className="font-medium">Provide Access Details</h4>
                  <p className="text-sm text-muted-foreground">
                    Customer shares their TeamViewer ID and password with our support team.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div>
                  <h4 className="font-medium">Remote Configuration</h4>
                  <p className="text-sm text-muted-foreground">
                    Our team connects remotely to configure the printer and test order printing.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  4
                </div>
                <div>
                  <h4 className="font-medium">Testing & Verification</h4>
                  <p className="text-sm text-muted-foreground">
                    Test order receipts are printed to ensure proper formatting and functionality.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <div>
                  <h4 className="font-medium">Download Setup Guide</h4>
                  <p className="text-sm text-muted-foreground">
                    Customer receives detailed printer setup documentation and drivers.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div>
                  <h4 className="font-medium">Install Printer Drivers</h4>
                  <p className="text-sm text-muted-foreground">
                    Install the appropriate drivers for their printer model.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div>
                  <h4 className="font-medium">Configure Settings</h4>
                  <p className="text-sm text-muted-foreground">
                    Follow the guide to configure printer settings and receipt templates.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  4
                </div>
                <div>
                  <h4 className="font-medium">Test & Verify</h4>
                  <p className="text-sm text-muted-foreground">
                    Run test prints to ensure everything is working correctly.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            
            {hasRequiredInfo && !isComplete && (
              <>
                <Button 
                  variant="default"
                  onClick={() => handleStatusUpdate(OnboardingStatus.AWAITING_CONFIRMATION)}
                >
                  Mark as Ready for Setup
                </Button>
                
                <Button 
                  variant="default"
                  onClick={() => handleStatusUpdate(OnboardingStatus.COMPLETE)}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Complete
                </Button>
              </>
            )}
            
            <Button 
              variant="outline"
              onClick={() => handleStatusUpdate(OnboardingStatus.NEEDS_ATTENTION)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Mark as Needs Attention
            </Button>
          </div>
          
          <div className="p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              {!hasRequiredInfo 
                ? formData.printer_setup_method === SetupMethod.REMOTE_ACCESS
                  ? "Provide TeamViewer access details to proceed with remote printer setup."
                  : "Customer will handle printer setup themselves using provided documentation."
                : formData.printer_setup_method === SetupMethod.REMOTE_ACCESS
                ? "TeamViewer details are ready. Schedule a remote session to complete printer setup."
                : "Setup method configured. Customer can proceed with self-guided printer setup."
              }
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrinterSetupTab;